// UI slice — top-bar / overlay toggles + side-panel layout. Spec §4.2, §6.1, §6.3.3.

import type { StateCreator } from 'zustand';
import type { AppState } from './index';
import type { Side } from './predictions';

export const SIDE_PANEL_DEFAULT_WIDTH = 240;
export const SIDE_PANEL_MIN_WIDTH = 160;
export const SIDE_PANEL_MAX_WIDTH = 480;

export interface UiSlice {
  showSkeleton: boolean;
  toggleSkeleton: () => void;

  // When true, keypoint drag ignores epipolar constraints (L-Shift toggles).
  freeDrag: boolean;
  toggleFreeDrag: () => void;

  // "All frames audited" completion dialog. Shared so both the Next button and
  // the Space (save & advance) shortcut open the same dialog.
  completionOpen: boolean;
  setCompletionOpen: (open: boolean) => void;

  sidePanelWidth: Record<Side, number>;
  sidePanelCollapsed: Record<Side, boolean>;
  setSidePanelWidth: (side: Side, widthPx: number) => void;
  toggleSidePanelCollapsed: (side: Side) => void;
}

function clampWidth(widthPx: number): number {
  return Math.max(SIDE_PANEL_MIN_WIDTH, Math.min(SIDE_PANEL_MAX_WIDTH, Math.round(widthPx)));
}

export const createUiSlice: StateCreator<AppState, [['zustand/immer', never]], [], UiSlice> = (
  set
) => ({
  showSkeleton: true,
  toggleSkeleton: () =>
    set((s) => {
      s.showSkeleton = !s.showSkeleton;
    }),

  freeDrag: false,
  toggleFreeDrag: () =>
    set((s) => {
      s.freeDrag = !s.freeDrag;
    }),

  completionOpen: false,
  setCompletionOpen: (open) =>
    set((s) => {
      s.completionOpen = open;
    }),

  sidePanelWidth: {
    front_left: SIDE_PANEL_DEFAULT_WIDTH,
    front_right: SIDE_PANEL_DEFAULT_WIDTH,
    left: SIDE_PANEL_DEFAULT_WIDTH,
    right: SIDE_PANEL_DEFAULT_WIDTH,
  },
  sidePanelCollapsed: { front_left: false, front_right: false, left: false, right: false },
  setSidePanelWidth: (side, widthPx) =>
    set((s) => {
      s.sidePanelWidth[side] = clampWidth(widthPx);
    }),
  toggleSidePanelCollapsed: (side) =>
    set((s) => {
      s.sidePanelCollapsed[side] = !s.sidePanelCollapsed[side];
    }),
});
